import { BehaviorSubject } from 'rxjs'
import type { SpacesService } from '@/spaces/services/SpacesService'
import type { SpaceGqlModel } from '@/spaces/gql/SpaceGqlModel'
import { assertLog, logError } from '@/common/errors/log-utils'
import type { CollectionsApiService } from './CollectionsApiService'
import type { CollectionTreeModel } from '../collections-tree/CollectionTreeModel'
import type { FlatCollectionTreeModel } from 'objects/gql/tree/FlatCollectionTreeModel.ts'
import type { CreateCollectionInput } from 'objects/gql/tree/CreateCollectionInput.ts'
import * as op from '../../common/errors/OperationResult'
import { OperationResult } from '@/common/errors/OperationResult'
import type { CreateFolderInput } from 'objects/gql/tree/CreateFolderInput.ts'
import type { IBehaviorObservable } from '@/common/react/IBehaviorObservable'
import { isCollection, isFolder } from '@/objects/gql/tree/HierarchyNodeModel.ts'
import type {
  CollectionModel,
  FolderModel,
  HierarchyNodeModel,
  UpdateCollectionInput,
  UpdateFolderInput,
} from '@/core/gql/gql-models-override.ts'
import { TypeChangeKind, type TypeChangeEvent } from '@/object-types/services/ObjectTypesService.ts'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'

export class CollectionsTreeService {
  public get collectionsTree(): CollectionTreeModel | undefined {
    return this._collectionsTree
  }

  public collectionsTreeVersion$: IBehaviorObservable<number>
  private collectionsTreeVersion: BehaviorSubject<number>
  private fetchAbortController?: AbortController
  private _collectionsTree: CollectionTreeModel | undefined

  constructor(
    private readonly spacesService: SpacesService,
    private readonly collectionsApiService: CollectionsApiService,
    private readonly objectTypesService: ObjectTypesService,
  ) {
    this.collectionsTreeVersion = new BehaviorSubject<number>(0)
    this.collectionsTreeVersion$ = this.collectionsTreeVersion
    this.spacesService.selectedSpace$.subscribe(this.onSelectedSpaceChanged.bind(this))
    this.objectTypesService.typeChanged$.subscribe(this.onTypeChanged.bind(this))
  }

  public async createCollection(
    input: CreateCollectionInput,
  ): Promise<OperationResult<CollectionModel>> {
    const { error, result: createdCollection } =
      await this.collectionsApiService.createCollection(input)

    if (error) {
      return op.error(error)
    }

    this.insertCreatedNode(createdCollection!)
    return op.success(createdCollection!)
  }

  public async updateCollection(
    input: UpdateCollectionInput,
  ): Promise<OperationResult<CollectionModel>> {
    const { error, result: updatedCollection } =
      await this.collectionsApiService.updateCollection(input)

    if (error) {
      return op.error(error)
    }

    assertLog(updatedCollection!.id == input.id)
    Object.assign(this._collectionsTree!.idToNode.get(updatedCollection!.id)!, updatedCollection!)
    return op.success(updatedCollection!)
  }

  public async deleteCollection(id: string): Promise<OperationResult> {
    const { error } = await this.collectionsApiService.deleteCollection(id)

    if (error) {
      logError('error deleting colelction', { id, message: error.message })
      return op.error(error)
    }

    await this.onNodeDeleted()
    return op.success()
  }

  public async createFolder(input: CreateFolderInput): Promise<OperationResult<FolderModel>> {
    const { error, result: createdFolder } = await this.collectionsApiService.createFolder(input)

    if (error) {
      return op.error(error)
    }

    this.insertCreatedNode(createdFolder!)
    return op.success(createdFolder!)
  }

  public async updateFolder(input: UpdateFolderInput): Promise<OperationResult<FolderModel>> {
    const { error, result: updatedFolder } = await this.collectionsApiService.updateFolder(input)

    if (error) {
      return op.error(error)
    }

    assertLog(updatedFolder!.id == input.id)
    Object.assign(this._collectionsTree!.idToNode.get(updatedFolder!.id)!, updatedFolder!)
    return op.success(updatedFolder!)
  }

  public async deleteFolder(id: string): Promise<OperationResult> {
    const { error } = await this.collectionsApiService.deleteFolder(id)

    if (error) {
      logError('error deleting folder', { id, message: error.message })
      return op.error(error)
    }

    await this.onNodeDeleted()
    return op.success()
  }

  private onTypeChanged(events: TypeChangeEvent[]): void {
    const tree = this._collectionsTree
    if (!tree) return

    let deleted = false
    for (const event of events) {
      if (event.kind !== TypeChangeKind.TypeDeleted) continue
      for (const node of tree.idToNode.values()) {
        if (!isCollection(node) || node.typeId !== event.typeId) continue
        tree.idToNode.delete(node.id)
        tree.idToSubtreeVersion.delete(node.id)
        const siblings = tree.nodeToChildren.get(node.parentId)!
        tree.nodeToChildren.set(node.parentId, siblings.filter((id) => id !== node.id))
        deleted = true
      }
    }
    if (!deleted) return

    this.collectionsTreeVersion.next(this.collectionsTreeVersion.value + 1)
  }

  private async onNodeDeleted() {
    await this.fetchTree(this.spacesService.selectedSpace$.value!.id)
  }

  private onSelectedSpaceChanged(spaces: SpaceGqlModel | undefined): void {
    void this.onSelectedSpaceChangedAsync(spaces)
  }

  private async onSelectedSpaceChangedAsync(
    selectedSpace: SpaceGqlModel | undefined,
  ): Promise<void> {
    if (!selectedSpace) {
      return
    }

    this.fetchAbortController?.abort()
    this.fetchAbortController = new AbortController()
    await this.fetchTree(selectedSpace.id)
  }

  private async fetchTree(spaceId: number): Promise<void> {
    const { error, result: flatTree } = await this.collectionsApiService.getCollectionsTree(
      spaceId,
      this.fetchAbortController!.signal,
    )

    if (error) {
      logError(`error fetching collection tree for space: ${error.message}`, { spaceId })
      return
    }

    const newVersion = this.collectionsTreeVersion.value + 1
    this._collectionsTree = this.buildCollectionTree(flatTree!, newVersion)
    this.collectionsTreeVersion.next(newVersion)
  }

  private buildCollectionTree(
    flatTree: FlatCollectionTreeModel,
    version: number,
  ): CollectionTreeModel {
    const idToNode = new Map<string, HierarchyNodeModel>()
    const nodeToChildren = new Map<string | null | undefined, string[]>()
    const nodeToVersion = new Map<string, number>()

    for (const node of flatTree.nodes) {
      idToNode.set(node.id, node)

      if (!nodeToChildren.has(node.parentId)) {
        nodeToChildren.set(node.parentId, [])
      }
      nodeToChildren.get(node.parentId)!.push(node.id)
      nodeToVersion.set(node.id, version)
    }

    for (const children of nodeToChildren.values()) {
      this.sortByTypeThenByName(children, idToNode)
    }

    return { idToNode, nodeToChildren, idToSubtreeVersion: nodeToVersion }
  }

  private sortByTypeThenByName(children: string[], idToNode: Map<string, HierarchyNodeModel>) {
    children.sort((a, b) => {
      const leftNode = idToNode.get(a)
      const rightNode = idToNode.get(b)

      if (!leftNode || !rightNode) return 0

      const leftIsFolder = isFolder(leftNode)
      const rightIsFolder = isFolder(rightNode)

      if (leftIsFolder && !rightIsFolder) return -1
      if (!leftIsFolder && rightIsFolder) return 1

      return leftNode.name.localeCompare(rightNode.name)
    })
  }

  private insertCreatedNode(newNode: HierarchyNodeModel) {
    const tree = this._collectionsTree!

    tree.idToNode.set(newNode.id, newNode)

    const currChildren = tree.nodeToChildren.get(newNode.parentId) || []
    const newChildren = [...currChildren, newNode.id]
    this.sortByTypeThenByName(newChildren, tree.idToNode)
    tree.nodeToChildren.set(newNode.parentId, newChildren)

    const newVersion = this.collectionsTreeVersion.value + 1
    this.setAncestorsVersions(newNode, newVersion)
    this.collectionsTreeVersion.next(newVersion)
  }

  private setAncestorsVersions(newNode: HierarchyNodeModel, newVersion: number) {
    const tree = this._collectionsTree!

    let node: HierarchyNodeModel = newNode
    while (true) {
      tree.idToSubtreeVersion.set(node.id, newVersion)
      if (!node.parentId) {
        break
      }
      node = tree.idToNode.get(node.parentId)!
    }
  }
}
